/** DESIGN.md §7: "Time as m:ss under an hour, h:mm:ss above." Shared by every screen that shows
 * a duration or a playback position — SongRow, VideoPlayer's transport clock, and later the
 * queue/rotation ETAs — so there is exactly one implementation of this rule. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—:—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** MVP-11 #17 — DESIGN §7 time rule applied to a rotation/queue ETA. One rendering everywhere:
 * `~m:ss`, or `—` when the server has nothing estimated. */
export function formatEta(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—';
  return `~${formatDuration(sec * 1000)}`;
}

/** MVP-11 #9: "Asked m:ss ago" (`formatDuration` reused for an elapsed age) reads absurd once a
 * card is hours old — "Asked 168:00:00 ago". Coarser, human buckets instead, same idea as every
 * social-feed relative timestamp: seconds -> "just now", minutes -> "N min ago", hours -> "N h
 * ago", a day or more -> "yesterday" / "N days ago". */
export function formatRelativeAge(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
